import React, {useEffect, useMemo} from 'react'
import Text from '../../components/text/Text'
import WizImage from '../../components/image/WizImage'
import WizButton from '../../components/button/WizButton'
import UnderLineInput from '../../components/input/UnderLineInput'
import DateInput from '../../components/input/DateInput'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import BannerPickerModal from './BannerPickerModal'
import {IVolumeLeague, LeagueKind, LeagueScopeType, LeagueStatus, LeagueVisibility} from '../../apis/volume_league/list'
import {createVolumeLeague} from '../../apis/volume_league/create'
import {editVolumeLeague} from '../../apis/volume_league/edit'
import {ICard, IFormBody} from './types'
import {FIXED_WINDOWS, MARKET_SUPPORT, MARKETS_EXAMPLE, REFRESH_OPTIONS, STATUS_META, STATUS_ORDER, WINDOW_PRESETS} from './constants'
import {isValidExchangeUrl, marketExchanges, parseMarkets, tfLabel, uniq} from './utils'

interface IProps {
    view: 'create' | 'edit'
    body: IFormBody
    setBody: React.Dispatch<React.SetStateAction<IFormBody>>
    exchanges?: any[]
    allLeagues: IVolumeLeague[]
    cards: ICard[]
    exchangeMeta: (name?: string | null) => any
    convertTimezoneToInput: (v: any) => any
    convertInputToTimezone: (v: any) => any
    onSubmitted: () => void // 생성/수정 성공 시 (목록으로 복귀 + refetch)
    onRequestDelete: () => void // 삭제 버튼 → 확인 모달 요청
}

/**
 * 생성/수정 폼 — ① 종류 → ② 집계 대상 → ③ 그룹 이름 → 기간/상금/거래소/갱신/순서/링크.
 * 폼 전용 파생값(검증·중복 기간 등)·핸들러·제출 로직은 이 컴포넌트가 직접 소유한다.
 * body/setBody 만 상위(index)와 공유 — body.editKey 가 카드 하이라이트/삭제에서 쓰이기 때문.
 */
const LeagueForm: React.FC<IProps> = ({
    view,
    body,
    setBody,
    exchanges,
    allLeagues,
    cards,
    exchangeMeta,
    convertTimezoneToInput,
    convertInputToTimezone,
    onSubmitted,
    onRequestDelete,
}) => {
    const isRolling = body.kind === 'rolling'
    const isSymbolScope = body.scope_type === 'symbol'
    const isMarketsScope = body.scope_type === 'markets'
    const isEdit = view === 'edit'

    // markets scope: 입력 JSON 을 파싱한 시장 목록과 그 거래소들. 거래소 링크는 이 거래소들과 매칭된다.
    const parsedMarkets = useMemo(() => (isMarketsScope ? parseMarkets(body.marketsText) : {}), [
        isMarketsScope,
        body.marketsText,
    ])
    const marketExs = useMemo(() => marketExchanges(parsedMarkets.markets), [parsedMarkets])
    // 거래소 링크를 받는 거래소 목록 — symbol: 집계 거래소 / markets: 시장 목록의 거래소 / exchange: 없음
    const linkExchanges = isSymbolScope ? body.supported_exchanges : isMarketsScope ? marketExs : []

    // 갱신 주기 기본값 — 같은 대상/같은 종류의 기존 리그가 있으면 그 값을 우선, 없으면 상시=600 / 대회=60
    const refreshDefaultFor = (kind: LeagueKind): number => {
        const sym = body.symbol.trim().toUpperCase()
        const sameKind = allLeagues.filter(l => l?.kind === kind && l?.refresh_interval_sec != null)
        const targeted = sameKind.filter(l =>
            isSymbolScope
                ? l?.scope_type === 'symbol' && !!sym && (l?.symbol || '').toUpperCase() === sym
                : l?.scope_type === 'exchange' && !!body.exchange && l?.exchange === body.exchange,
        )
        const pick = targeted[0] || sameKind[0]
        return (pick?.refresh_interval_sec as number) ?? (kind === 'competition' ? 60 : 600)
    }

    // 생성 시: 현재 타깃(거래소/심볼)에 이미 존재하는 rolling window_days (중복 방지)
    const takenWindows = useMemo<number[]>(() => {
        if (view !== 'create') return []
        const sym = body.symbol.trim().toUpperCase()
        return allLeagues
            .filter(l =>
                l?.kind === 'rolling' && isSymbolScope
                    ? l?.scope_type === 'symbol' && !!sym && (l?.symbol || '').toUpperCase() === sym
                    : l?.kind === 'rolling' &&
                      l?.scope_type === 'exchange' &&
                      !!body.exchange &&
                      l?.exchange === body.exchange,
            )
            .map(l => l?.window_days)
            .filter((d): d is number => d != null && d > 0)
    }, [view, allLeagues, isSymbolScope, body.symbol, body.exchange])

    // 타깃이 바뀌면 기본 기간 세트를 "이미 있는 기간 제외한 고정 기간"으로 재설정 (생성 시, rolling)
    useEffect(() => {
        if (view !== 'create' || !isRolling) return
        const avail = FIXED_WINDOWS.filter(w => !takenWindows.includes(w))
        setBody(prev => ({...prev, windows: avail}))
    }, [view, isRolling, body.scope_type, body.symbol, body.exchange, takenWindows, setBody])

    const toggleWindow = (wd: number) => {
        if (FIXED_WINDOWS.includes(wd)) return // 고정 기간은 해제 불가
        if (takenWindows.includes(wd)) return // 이미 존재하는 기간은 선택 불가
        setBody(prev => ({
            ...prev,
            windows: prev.windows.includes(wd) ? prev.windows.filter(w => w !== wd) : [...prev.windows, wd],
        }))
    }
    const toggleSupported = (name: string) => {
        setBody(prev => {
            const has = prev.supported_exchanges.includes(name)
            // 거래소 해제 시 매칭 유지를 위해 그 거래소의 링크도 제거
            const nextUrls = {...prev.exchange_urls}
            if (has) delete nextUrls[name]
            return {
                ...prev,
                supported_exchanges: has
                    ? prev.supported_exchanges.filter(e => e !== name)
                    : [...prev.supported_exchanges, name],
                exchange_urls: nextUrls,
            }
        })
    }
    // All Exchange 토글 — 모두 선택되어 있으면 비우고(빈 배열), 아니면 전체 거래소 선택
    // ('All Exchange' 자체는 배열에 넣지 않고, 실제 거래소 이름만 채운다)
    const toggleAllExchanges = () => {
        setBody(prev => {
            const names = (exchanges || []).map(e => e?.name as string).filter(Boolean)
            const all = names.length > 0 && names.every(n => prev.supported_exchanges.includes(n))
            if (all) return {...prev, supported_exchanges: [], exchange_urls: {}}
            return {...prev, supported_exchanges: names}
        })
    }

    // 거래소별 링크 입력 (빈 값이면 키 제거)
    const setExchangeUrl = (name: string, url: string) => {
        setBody(prev => {
            const nextUrls = {...prev.exchange_urls}
            if (url.trim()) nextUrls[name] = url.trim()
            else delete nextUrls[name]
            return {...prev, exchange_urls: nextUrls}
        })
    }

    // 기간 대회 검증용
    const periodValid =
        !!body.start_at && !!body.end_at && new Date(body.start_at).getTime() < new Date(body.end_at).getTime()

    const missing = useMemo<string[]>(() => {
        const m: string[] = []
        if (!body.baseName.trim()) m.push(isRolling ? '세트 이름' : '대회 이름')
        if (!isEdit) {
            if (isSymbolScope && !body.symbol.trim()) m.push('코인(심볼)')
            if (body.scope_type === 'exchange' && !body.exchange) m.push('거래소')
        }
        if (isMarketsScope && !parsedMarkets.markets) m.push('시장 목록')
        if (isRolling) {
            if (body.windows.length === 0) m.push('기간 세트')
        } else {
            if (!periodValid) m.push('기간 (시작 < 종료)')
            if (body.prizeAmount > 0 && !body.prizeCurrency.trim()) m.push('상금 통화')
        }
        // 집계 거래소(symbol scope) 는 필수. Exchange Links 는 선택 — 누락 시 제출 단계에서 모달로 확인.
        if (isSymbolScope && body.supported_exchanges.length === 0) m.push('집계 거래소')
        return m
    }, [body, isRolling, isSymbolScope, isMarketsScope, parsedMarkets, isEdit, periodValid])
    const canSubmit = missing.length === 0

    // Exchange Link 누락 경고 모달 대상 (symbol scope, 비어있는 집계 거래소 목록)
    const [linkWarn, setLinkWarn] = React.useState<string[] | null>(null)

    // Slot 가져오기 모달 — 어느 필드(slot/mobile) 의 어느 언어(ko/en) 버튼을 눌렀는지
    const [picker, setPicker] = React.useState<{
        field: 'slot_id' | 'mobile_slot_id'
        lang: 'ko' | 'en'
    } | null>(null)
    // 모달에서 고른 item id 를 해당 필드/언어에만 적용
    const applyPicked = (id: string) => {
        if (!picker) return
        setBody(prev => ({...prev, [picker.field]: {...prev[picker.field], [picker.lang]: id}}))
    }

    // 제출 게이트: symbol scope 에서 링크가 빠진 집계 거래소가 있으면 확인 모달 → 확인 시 실제 제출
    const submit = () => {
        if (!canSubmit) return
        if (linkExchanges.length > 0) {
            const missingLinks = linkExchanges.filter(ex => !isValidExchangeUrl(body.exchange_urls[ex]))
            if (missingLinks.length > 0) {
                setLinkWarn(missingLinks)
                return
            }
        }
        doSubmit()
    }

    const doSubmit = async () => {
        if (!canSubmit) return
        const prize =
            body.prizeAmount > 0 && body.prizeCurrency
                ? {total_amount: body.prizeAmount, currency: body.prizeCurrency}
                : undefined
        // markets scope 는 시장 목록의 거래소를 표시용 supported_exchanges 로 보낸다 (집계는 markets 가 결정).
        // 예전엔 symbol 이 아니면 [body.exchange] 를 보내서, exchange 가 빈 markets 리그 수정이 [""] 로 깨졌다.
        const supported = isSymbolScope ? body.supported_exchanges : isMarketsScope ? marketExs : [body.exchange]
        const markets = isMarketsScope ? parsedMarkets.markets : undefined
        // exchange_urls — 링크 대상 거래소(linkExchanges)에 매칭되는 링크만 정리해서 전송
        const exchangeUrls: {[k: string]: string} = {}
        linkExchanges.forEach(ex => {
            const v = (body.exchange_urls[ex] || '').trim()
            if (v) exchangeUrls[ex] = v
        })
        const hasUrls = Object.keys(exchangeUrls).length > 0

        // Slot 식별자 — 선택값. 둘 중 하나라도 있으면 전송, 없으면 생략(생성) / 빈 값으로 초기화(수정)
        const slotKo = (body.slot_id.ko || '').trim()
        const slotEn = (body.slot_id.en || '').trim()
        const hasSlot = !!(slotKo || slotEn)
        const slotId = {ko: slotKo, en: slotEn}

        // 모바일 Slot 식별자 — slot_id 와 동일 규칙 (device=mo 기준)
        const mSlotKo = (body.mobile_slot_id.ko || '').trim()
        const mSlotEn = (body.mobile_slot_id.en || '').trim()
        const hasMobileSlot = !!(mSlotKo || mSlotEn)
        const mobileSlotId = {ko: mSlotKo, en: mSlotEn}

        if (view === 'create') {
            if (isRolling) {
                // 선택 기간 만큼 레코드 생성 (세트)
                const wins = uniq(body.windows).sort((a, b) => a - b)
                const results = await Promise.all(
                    wins.map(wd =>
                        createVolumeLeague({
                            name: `${body.baseName.trim()} ${tfLabel(wd)}`,
                            kind: 'rolling',
                            scope_type: body.scope_type,
                            symbol: isSymbolScope ? body.symbol.trim().toUpperCase() : undefined,
                            exchange: body.scope_type === 'exchange' ? body.exchange : undefined,
                            markets,
                            visibility: body.visibility,
                            supported_exchanges: supported,
                            exchange_urls: hasUrls ? exchangeUrls : undefined,
                            window_days: wd,
                            refresh_interval_sec: Number(body.refresh_interval_sec) || undefined,
                            url_third_party: body.url_third_party || '',
                            sort_value: Number(body.sort_value) || undefined,
                            slot_id: hasSlot ? slotId : undefined,
                            mobile_slot_id: hasMobileSlot ? mobileSlotId : undefined,
                        }),
                    ),
                )
                if (results.every(r => r?.status === 200)) {
                    alert(`상시 순위표 세트 생성 완료 : ${body.baseName} (${wins.map(tfLabel).join(', ')})`)
                    onSubmitted()
                } else {
                    alert(results.find(r => r?.status !== 200)?.error || '일부 기간 생성에 실패했습니다.')
                }
            } else {
                const result = await createVolumeLeague({
                    name: body.baseName.trim(),
                    kind: 'competition',
                    scope_type: body.scope_type,
                    symbol: isSymbolScope ? body.symbol.trim().toUpperCase() : undefined,
                    exchange: body.scope_type === 'exchange' ? body.exchange : undefined,
                    markets,
                    visibility: body.visibility,
                    supported_exchanges: supported,
                    exchange_urls: hasUrls ? exchangeUrls : undefined,
                    prize_pool: prize,
                    start_at: body.start_at,
                    end_at: body.end_at,
                    refresh_interval_sec: Number(body.refresh_interval_sec) || undefined,
                    url_third_party: body.url_third_party || '',
                    sort_value: Number(body.sort_value) || undefined,
                    slot_id: hasSlot ? slotId : undefined,
                    mobile_slot_id: hasMobileSlot ? mobileSlotId : undefined,
                })
                if (result?.status === 200) {
                    alert(`기간 대회 생성 완료 : ${body.baseName}`)
                    onSubmitted()
                } else {
                    alert(result?.error || result?.Message || `생성 실패 (status: ${result?.status})`)
                }
            }
            return
        }

        // ----- 수정 -----
        // 세트의 각 레코드를 공통 필드로 일괄 업데이트 (종류·대상·기간(window)은 변경 불가)
        const card = cards.find(c => c.key === body.editKey)
        if (!card) return
        const updates = card.items.map(l =>
            editVolumeLeague({
                id: l.id,
                name: isRolling ? `${body.baseName.trim()} ${tfLabel(l?.window_days)}` : body.baseName.trim(),
                status: body.status,
                visibility: body.visibility,
                markets, // markets scope 면 시장 목록 전체 교체, 그 외엔 생략
                supported_exchanges: supported,
                // symbol·markets scope 면 링크 동기화({} 전달 시 제거), exchange scope 면 건드리지 않음
                exchange_urls: isSymbolScope || isMarketsScope ? exchangeUrls : undefined,
                refresh_interval_sec: Number(body.refresh_interval_sec) || undefined,
                url_third_party: body.url_third_party || '',
                sort_value: Number(body.sort_value) || 0,
                slot_id: slotId, // 빈 값 전달 시 초기화
                mobile_slot_id: mobileSlotId, // 빈 값 전달 시 초기화
                ...(isRolling
                    ? {}
                    : {
                          start_at: body.start_at,
                          end_at: body.end_at,
                          prize_pool: prize || {total_amount: 0, currency: ''},
                      }),
            }),
        )
        const results = await Promise.all(updates)
        if (results.every(r => r?.status === 200)) {
            alert(`수정 완료 : ${body.baseName}`)
            onSubmitted()
        } else {
            alert(results.find(r => r?.status !== 200)?.error || '일부 수정에 실패했습니다.')
        }
    }

    return (
        <>
            <div className="w-[1px] bg-line02" />
            <article className="w-full max-w-[560px]">
                <Text className="text-h2 text-font01">{view === 'create' ? '새 리그 만들기' : '리그 수정'}</Text>
                <div className="mt-4 p-6 border border-line02 bg-white rounded-[6px] space-y-[30px]">
                    {/* ① 종류 */}
                    <section>
                        <Text className="text-h3 text-font01">
                            ① 종류 <span className="text-font_red">*</span>
                        </Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            {isEdit ? '생성 후 변경 불가.' : '먼저 고르세요. 생성 후 변경 불가.'}
                        </Text>
                        <div className="flex items-center gap-x-[10px] mt-[10px]">
                            {[
                                {
                                    id: 'rolling' as LeagueKind,
                                    t: '상시 순위표',
                                    d: '24H·7D·30D 거래량 순위를 상시 운영',
                                },
                                {
                                    id: 'competition' as LeagueKind,
                                    t: '기간 대회',
                                    d: '시작~종료 기간이 정해진 대회(상금 가능)',
                                },
                            ].map(opt => {
                                const selected = body.kind === opt.id
                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() =>
                                            !isEdit &&
                                            setBody(prev => ({
                                                ...prev,
                                                kind: opt.id,
                                                refresh_interval_sec: refreshDefaultFor(opt.id),
                                            }))
                                        }
                                        className={`flex-1 border py-[10px] px-[14px] rounded-[5px] ${
                                            isEdit ? 'cursor-default' : 'cursor-pointer'
                                        } ${selected ? 'bg-primary border-primary' : 'border-line02'} ${
                                            isEdit && !selected ? 'opacity-40' : ''
                                        }`}>
                                        <Text className={`font-bold ${selected ? 'text-white' : 'text-font01'}`}>
                                            {opt.t}
                                        </Text>
                                        <Text className={`text-body3 ${selected ? 'text-white' : 'text-font02'}`}>
                                            {opt.d}
                                        </Text>
                                    </div>
                                )
                            })}
                        </div>
                    </section>

                    {/* ② 집계 대상 */}
                    <section>
                        <Text className="text-h3 text-font01">
                            ② 집계 대상 <span className="text-font_red">*</span>
                        </Text>
                        <Text className="text-body3 text-font02 mt-[4px]">무엇의 거래량으로 순위를 매길지</Text>
                        <div className="flex items-center gap-x-[10px] mt-[10px]">
                            {[
                                {
                                    id: 'exchange' as LeagueScopeType,
                                    t: '거래소 기준',
                                    d: '특정 거래소 전체 거래량',
                                },
                                {
                                    id: 'symbol' as LeagueScopeType,
                                    t: '코인 기준',
                                    d: '특정 코인(심볼) 거래량',
                                },
                                {
                                    id: 'markets' as LeagueScopeType,
                                    t: '시장 지정',
                                    d: '거래소·현물/선물·마켓을 직접 지정',
                                },
                            ].map(opt => {
                                const selected = body.scope_type === opt.id
                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() =>
                                            !isEdit &&
                                            setBody(prev => ({
                                                ...prev,
                                                scope_type: opt.id,
                                                symbol: opt.id === 'symbol' ? prev.symbol : '',
                                                exchange: opt.id === 'exchange' ? prev.exchange : '',
                                                marketsText: opt.id === 'markets' ? prev.marketsText : '',
                                            }))
                                        }
                                        className={`flex-1 border py-[10px] px-[14px] rounded-[5px] ${
                                            isEdit ? 'cursor-default' : 'cursor-pointer'
                                        } ${selected ? 'bg-primary border-primary' : 'border-line02'} ${
                                            isEdit && !selected ? 'opacity-40' : ''
                                        }`}>
                                        <Text className={`font-bold ${selected ? 'text-white' : 'text-font01'}`}>
                                            {opt.t}
                                        </Text>
                                        <Text className={`text-body3 ${selected ? 'text-white' : 'text-font02'}`}>
                                            {opt.d}
                                        </Text>
                                    </div>
                                )
                            })}
                        </div>

                        {/* 대상 입력 */}
                        {isMarketsScope ? (
                            <div className="mt-[12px]">
                                <Text className="text-ti2 text-font01">시장 목록 (JSON)</Text>
                                <Text className="text-body3 text-font02 mt-[2px]">
                                    여기 적은 시장의 주문만 집계합니다. 현물은 symbol=코인 + quote, 선물은 symbol=계약명
                                    (quote 비움). 수정 시 목록 전체가 교체됩니다.
                                </Text>
                                <Text className="text-body3 text-font03 mt-[2px]">
                                    지원:{' '}
                                    {Object.keys(MARKET_SUPPORT)
                                        .map(ex => `${ex} (${MARKET_SUPPORT[ex].join('/')})`)
                                        .join(' · ')}
                                </Text>
                                <textarea
                                    value={body.marketsText}
                                    placeholder={MARKETS_EXAMPLE}
                                    spellCheck={false}
                                    rows={8}
                                    onChange={e => {
                                        const text = e.target.value
                                        setBody(prev => ({...prev, marketsText: text}))
                                    }}
                                    className="mt-[8px] w-full border border-line02 rounded-[5px] p-[10px] font-mono text-body3"
                                />
                                {body.marketsText.trim() === '' && (
                                    <div
                                        onClick={() => setBody(prev => ({...prev, marketsText: MARKETS_EXAMPLE}))}
                                        className="inline-block mt-[4px] border border-line02 rounded-[5px] px-[10px] py-[4px] cursor-pointer">
                                        <Text className="text-body3">예시 채우기</Text>
                                    </div>
                                )}
                                {parsedMarkets.error && body.marketsText.trim() !== '' && (
                                    <Text className="text-body3 text-font_red mt-[4px]">{parsedMarkets.error}</Text>
                                )}
                                {parsedMarkets.markets && (
                                    <div className="mt-[6px] space-y-[2px]">
                                        {parsedMarkets.markets.map(m => (
                                            <Text
                                                key={`${m.exchange}|${m.type}|${m.symbol}|${m.quote || ''}`}
                                                className="text-body3 text-font01">
                                                ✓ {exchangeMeta(m.exchange)?.display_name || m.exchange} ·{' '}
                                                {m.type === 'spot' ? `현물 ${m.symbol}/${m.quote}` : `선물 ${m.symbol}`}
                                            </Text>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : isSymbolScope ? (
                            <div className="mt-[12px]">
                                <Text className="text-ti2 text-font01">코인(심볼)</Text>
                                <Text className="text-body3 text-font02 mt-[2px]">
                                    기본 자산만 입력 — BTC = BTCUSDT = BTC-USDT = XBT 자동 병합.
                                </Text>
                                {isEdit ? (
                                    <Text className="text-body1 font-bold text-font01 mt-[4px]">{body.symbol || '-'}</Text>
                                ) : (
                                    <UnderLineInput
                                        inputClassName={'w-full text-body2'}
                                        value={body.symbol}
                                        placeholder={'예: BTC'}
                                        onChanged={t => setBody(prev => ({...prev, symbol: t.toUpperCase()}))}
                                    />
                                )}
                            </div>
                        ) : (
                            <div className="mt-[12px]">
                                <Text className="text-ti2 text-font01">거래소</Text>
                                {isEdit ? (
                                    <div className="flex items-center gap-x-[8px] mt-[6px]">
                                        <WizImage src={exchangeMeta(body.exchange)?.icon as string} className="w-5 h-5" />
                                        <Text className="text-body1 font-bold text-font01">
                                            {exchangeMeta(body.exchange)?.display_name || body.exchange || '-'}
                                        </Text>
                                    </div>
                                ) : (
                                    <div className="mt-[6px] flex flex-wrap gap-[8px]">
                                        {exchanges?.map(ex => {
                                            const selected = body.exchange === ex?.name
                                            return (
                                                <div
                                                    key={ex?.name}
                                                    onClick={() =>
                                                        setBody(prev => ({...prev, exchange: ex?.name as string}))
                                                    }
                                                    className={`flex items-center gap-x-[6px] border py-[8px] px-[12px] rounded-[5px] cursor-pointer ${
                                                        selected ? 'bg-primary border-primary' : 'border-line02'
                                                    }`}>
                                                    <WizImage src={ex?.icon as string} className="w-4 h-4" />
                                                    <Text className={`${selected ? 'text-white' : ''}`}>
                                                        {ex?.display_name}
                                                    </Text>
                                                </div>
                                            )
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </section>

                    {/* ③ 그룹 이름 */}
                    <section>
                        <Text className="text-h3 text-font01">
                            ③ 그룹 이름 <span className="text-font_red">*</span>
                        </Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            거래소 또는 토큰의 구분을 위한 그룹의 이름 기준입니다.
                        </Text>
                        {isRolling && (
                            <Text className="text-body3 text-font03 mt-[2px]">
                                상시 순위표는 각 기간이 "Tenbagger 24H"처럼 자동 생성됩니다.
                            </Text>
                        )}
                        <UnderLineInput
                            inputClassName={'w-full text-body2'}
                            value={body.baseName}
                            placeholder={'예: Tenbagger, BTC'}
                            onChanged={t => setBody(prev => ({...prev, baseName: t}))}
                        />
                    </section>

                    {/* 기간 세트 (rolling) — 생성 시에만. 수정 시엔 변경 불가라 UI 에서 제외 */}
                    {isRolling && !isEdit && (
                        <section>
                            <Text className="text-h3 text-font01">기간 세트</Text>
                            <Text className="text-body3 text-font02 mt-[4px]">
                                24H · 7D · 30D 는 기본 포함. 90D · 180D 는 선택. 이미 있는 기간은 선택할 수 없어요.
                            </Text>
                            <div className="flex items-center gap-x-[10px] mt-[10px] flex-wrap gap-y-[8px]">
                                {WINDOW_PRESETS.map(p => {
                                    const taken = takenWindows.includes(p.wd)
                                    const selected = !taken && body.windows.includes(p.wd)
                                    return (
                                        <div
                                            key={p.wd}
                                            onClick={() => toggleWindow(p.wd)}
                                            className={`border py-[8px] px-[16px] rounded-[5px] flex items-center gap-x-[4px] ${
                                                taken
                                                    ? 'cursor-not-allowed opacity-40 bg-line03 border-line02'
                                                    : p.fixed
                                                    ? 'cursor-default'
                                                    : 'cursor-pointer'
                                            } ${selected ? 'bg-primary border-primary' : taken ? '' : 'border-line02'}`}>
                                            <Text
                                                className={`${
                                                    selected ? 'text-white' : taken ? 'text-font03 line-through' : ''
                                                }`}>
                                                {p.label}
                                            </Text>
                                            {p.fixed && !taken && (
                                                <Text className={`text-ti3 ${selected ? 'text-white' : 'text-font03'}`}>
                                                    🔒
                                                </Text>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                            <Text className="text-body3 text-font03 mt-[6px]">
                                이 세트는 {uniq(body.windows).length}개 레코드로 생성됩니다.
                                {takenWindows.length > 0 &&
                                    ` (이미 있는 기간: ${[...takenWindows]
                                        .sort((a, b) => a - b)
                                        .map(tfLabel)
                                        .join(', ')} 제외됨)`}
                            </Text>
                        </section>
                    )}

                    {/* 기간 + 상금 (competition) */}
                    {!isRolling && (
                        <>
                            <section>
                                <Text className="text-h3 text-font01">
                                    기간 <span className="text-font_red">*</span>
                                </Text>
                                <Text className="text-body3 text-font02 mt-[4px]">시작 &lt; 종료 (KST 기준)</Text>
                                <div className="flex gap-x-[20px] mt-[6px]">
                                    <div>
                                        <Text className="text-body3 text-font02">시작</Text>
                                        <DateInput
                                            value={convertTimezoneToInput(body.start_at)}
                                            onChange={e =>
                                                setBody(prev => ({
                                                    ...prev,
                                                    start_at: convertInputToTimezone(e.target.value) as string,
                                                }))
                                            }
                                        />
                                    </div>
                                    <div>
                                        <Text className="text-body3 text-font02">종료</Text>
                                        <DateInput
                                            value={convertTimezoneToInput(body.end_at)}
                                            onChange={e =>
                                                setBody(prev => ({
                                                    ...prev,
                                                    end_at: convertInputToTimezone(e.target.value) as string,
                                                }))
                                            }
                                        />
                                    </div>
                                </div>
                                {body.start_at && body.end_at && !periodValid && (
                                    <Text className="text-body3 text-font_red mt-[5px]">
                                        종료가 시작보다 뒤여야 합니다.
                                    </Text>
                                )}
                            </section>
                            <section>
                                <Text className="text-h3 text-font01">상금 (선택)</Text>
                                <div className="flex items-center gap-x-[20px] mt-[6px]">
                                    <div>
                                        <Text className="text-body3 text-font02">금액</Text>
                                        <UnderLineInput
                                            inputClassName={'w-full text-body2'}
                                            type="number"
                                            value={body.prizeAmount}
                                            onChanged={t => setBody(prev => ({...prev, prizeAmount: Number(t)}))}
                                        />
                                    </div>
                                    <div>
                                        <Text className="text-body3 text-font02">
                                            통화
                                            {body.prizeAmount > 0 && <span className="text-font_red"> *</span>}
                                        </Text>
                                        <UnderLineInput
                                            inputClassName={'w-full text-body2'}
                                            value={body.prizeCurrency}
                                            placeholder={'예: USDT'}
                                            onChanged={t => setBody(prev => ({...prev, prizeCurrency: t.toUpperCase()}))}
                                        />
                                    </div>
                                </div>
                            </section>
                        </>
                    )}

                    {/* 집계 거래소 (symbol scope) — 필수 */}
                    {isSymbolScope &&
                        (() => {
                            const allNames = (exchanges || []).map(e => e?.name as string).filter(Boolean)
                            const isAllSelected =
                                allNames.length > 0 && allNames.every(n => body.supported_exchanges.includes(n))
                            return (
                                <section>
                                    <Text className="text-h3 text-font01">
                                        집계 거래소 <span className="text-font_red">*</span>
                                    </Text>
                                    <div className="mt-[8px] flex flex-wrap gap-[10px]">
                                        {/* All Exchange — 전체 선택/해제 (배열엔 실제 거래소만 들어감) */}
                                        <div
                                            onClick={toggleAllExchanges}
                                            className={`flex items-center gap-x-[6px] border py-[8px] px-[12px] rounded-[5px] cursor-pointer ${
                                                isAllSelected ? 'bg-primary border-primary' : 'border-line02'
                                            }`}>
                                            <Text className={`${isAllSelected ? 'text-white' : ''}`}>All Exchange</Text>
                                        </div>
                                        {exchanges?.map(ex => {
                                            const checked = body.supported_exchanges.includes(ex?.name as string)
                                            return (
                                                <div
                                                    key={ex?.name}
                                                    onClick={() => toggleSupported(ex?.name as string)}
                                                    className={`flex items-center gap-x-[6px] border py-[8px] px-[12px] rounded-[5px] cursor-pointer ${
                                                        checked ? 'bg-primary border-primary' : 'border-line02'
                                                    }`}>
                                                    <Text className={`${checked ? 'text-white' : ''}`}>
                                                        {ex?.display_name}
                                                    </Text>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </section>
                            )
                        })()}

                    {/* Exchange Links — 집계 거래소(symbol scope) 또는 시장 목록의 거래소(markets scope) 와 1:1 매칭 */}
                    {linkExchanges.length > 0 && (
                        <section>
                            <Text className="text-h3 text-font01">Exchange Links</Text>
                            <Text className="text-body3 text-font02 mt-[4px]">
                                {isMarketsScope ? '시장 목록의 거래소' : '선택한 집계 거래소'}마다 링크(절대 http/https
                                URL)를 입력하세요. 1:1 매칭 필수 — 빠지거나 매칭 안 되면 저장 불가.
                            </Text>
                            <div className="mt-[10px] space-y-[12px]">
                                {/* 집계 거래소 버튼 순서(exchanges)대로 표시 */}
                                {exchanges
                                    ?.filter(ex => linkExchanges.includes(ex?.name as string))
                                    .map(ex => {
                                        const name = ex?.name as string
                                        const url = body.exchange_urls[name] || ''
                                        const invalid = url.length > 0 && !isValidExchangeUrl(url)
                                        return (
                                            <div key={name}>
                                                <div className="flex items-center gap-x-[8px]">
                                                    <WizImage src={ex?.icon as string} className="w-4 h-4" />
                                                    <Text className="text-body2 text-font01 font-bold">
                                                        {ex?.display_name || name}
                                                    </Text>
                                                </div>
                                                <UnderLineInput
                                                    inputClassName={'w-full text-body2'}
                                                    value={url}
                                                    placeholder={'https://...'}
                                                    onChanged={t => setExchangeUrl(name, t)}
                                                />
                                                {invalid && (
                                                    <Text className="text-body3 text-font_red mt-[4px]">
                                                        절대 http/https URL (최대 512자) 형식이어야 합니다.
                                                    </Text>
                                                )}
                                            </div>
                                        )
                                    })}
                            </div>
                        </section>
                    )}

                    {/* 갱신 주기 */}
                    <section>
                        <Text className="text-h3 text-font01">갱신 주기</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            코인/상시형은 600초, 대회는 60초 권장 (단위: 초)
                        </Text>
                        <div className="flex items-center gap-x-[10px] mt-[8px] flex-wrap gap-y-[8px]">
                            {REFRESH_OPTIONS.map(sec => (
                                <div
                                    key={sec}
                                    onClick={() => setBody(prev => ({...prev, refresh_interval_sec: sec}))}
                                    className={`border py-[8px] px-[16px] rounded-[5px] cursor-pointer ${
                                        body.refresh_interval_sec === sec ? 'bg-primary border-primary' : 'border-line02'
                                    }`}>
                                    <Text className={`${body.refresh_interval_sec === sec ? 'text-white' : ''}`}>
                                        {sec}s
                                    </Text>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* 상태 (수정 시) */}
                    {isEdit && (
                        <section>
                            <Text className="text-h3 text-font01">상태</Text>
                            <select
                                value={body.status}
                                onChange={e => setBody(prev => ({...prev, status: e.target.value as LeagueStatus}))}
                                className="mt-[6px] border border-line02 rounded-[5px] px-3 py-[6px] text-body2">
                                {STATUS_ORDER.map(s => (
                                    <option key={s} value={s}>
                                        {STATUS_META[s].label}
                                    </option>
                                ))}
                            </select>
                        </section>
                    )}

                    {/* 공개 범위 */}
                    <section>
                        <Text className="text-h3 text-font01">공개 범위</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            관리자 전용은 관리자 계정에만 보입니다 (준비·테스트용). 대회 순위 알림은 공개 리그에만
                            나갑니다.
                        </Text>
                        <div className="flex items-center gap-x-[10px] mt-[8px]">
                            {[
                                {id: 'public' as LeagueVisibility, t: '공개'},
                                {id: 'managers' as LeagueVisibility, t: '관리자 전용'},
                            ].map(opt => {
                                const selected = body.visibility === opt.id
                                return (
                                    <div
                                        key={opt.id}
                                        onClick={() => setBody(prev => ({...prev, visibility: opt.id}))}
                                        className={`border py-[8px] px-[16px] rounded-[5px] cursor-pointer ${
                                            selected ? 'bg-primary border-primary' : 'border-line02'
                                        }`}>
                                        <Text className={`${selected ? 'text-white' : ''}`}>{opt.t}</Text>
                                    </div>
                                )
                            })}
                        </div>
                    </section>

                    {/* 표시 순서 */}
                    <section>
                        <Text className="text-h3 text-font01">표시 순서 (Sort Value)</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            숫자가 클수록 목록 위에 노출됩니다. 같은 세트는 같은 값으로 통일됩니다. (0 = 최신순)
                        </Text>
                        <div className="w-[120px]">
                            <UnderLineInput
                                inputClassName={'w-full text-body2'}
                                type="number"
                                value={body.sort_value}
                                placeholder={'0'}
                                onChanged={t => setBody(prev => ({...prev, sort_value: Math.max(0, Number(t) || 0)}))}
                            />
                        </div>
                    </section>

                    {/* Slot 식별자 (PC) */}
                    <section>
                        <Text className="text-h3 text-font01">Slot 식별자 (선택)</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            slot_id — 언어별 PC Slot 식별자. "가져오기"로 프로모션의 tradermap-card(PC) 항목 id를 채울 수
                            있어요.
                        </Text>
                        <div className="mt-[10px] space-y-[10px]">
                            <div className="flex items-center gap-x-[10px]">
                                <Text className="text-body2 text-font02 w-[36px] shrink-0">KO</Text>
                                <div className="flex-1">
                                    <UnderLineInput
                                        inputClassName={'w-full text-body2'}
                                        value={body.slot_id.ko}
                                        placeholder={'한국어 Slot 식별자'}
                                        onChanged={t =>
                                            setBody(prev => ({...prev, slot_id: {...prev.slot_id, ko: t}}))
                                        }
                                    />
                                </div>
                                <WizButton
                                    text={'가져오기'}
                                    type="border"
                                    size="small"
                                    onClick={() => setPicker({field: 'slot_id', lang: 'ko'})}
                                />
                            </div>
                            <div className="flex items-center gap-x-[10px]">
                                <Text className="text-body2 text-font02 w-[36px] shrink-0">EN</Text>
                                <div className="flex-1">
                                    <UnderLineInput
                                        inputClassName={'w-full text-body2'}
                                        value={body.slot_id.en}
                                        placeholder={'English slot id'}
                                        onChanged={t =>
                                            setBody(prev => ({...prev, slot_id: {...prev.slot_id, en: t}}))
                                        }
                                    />
                                </div>
                                <WizButton
                                    text={'가져오기'}
                                    type="border"
                                    size="small"
                                    onClick={() => setPicker({field: 'slot_id', lang: 'en'})}
                                />
                            </div>
                        </div>
                    </section>

                    {/* 모바일 Slot 식별자 */}
                    <section>
                        <Text className="text-h3 text-font01">모바일 Slot 식별자 (선택)</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">
                            mobile_slot_id — 언어별 모바일 Slot 식별자. "가져오기"로 프로모션의 tradermap-card(모바일)
                            항목 id를 채울 수 있어요.
                        </Text>
                        <div className="mt-[10px] space-y-[10px]">
                            <div className="flex items-center gap-x-[10px]">
                                <Text className="text-body2 text-font02 w-[36px] shrink-0">KO</Text>
                                <div className="flex-1">
                                    <UnderLineInput
                                        inputClassName={'w-full text-body2'}
                                        value={body.mobile_slot_id.ko}
                                        placeholder={'한국어 모바일 Slot 식별자'}
                                        onChanged={t =>
                                            setBody(prev => ({
                                                ...prev,
                                                mobile_slot_id: {...prev.mobile_slot_id, ko: t},
                                            }))
                                        }
                                    />
                                </div>
                                <WizButton
                                    text={'가져오기'}
                                    type="border"
                                    size="small"
                                    onClick={() => setPicker({field: 'mobile_slot_id', lang: 'ko'})}
                                />
                            </div>
                            <div className="flex items-center gap-x-[10px]">
                                <Text className="text-body2 text-font02 w-[36px] shrink-0">EN</Text>
                                <div className="flex-1">
                                    <UnderLineInput
                                        inputClassName={'w-full text-body2'}
                                        value={body.mobile_slot_id.en}
                                        placeholder={'English mobile slot id'}
                                        onChanged={t =>
                                            setBody(prev => ({
                                                ...prev,
                                                mobile_slot_id: {...prev.mobile_slot_id, en: t},
                                            }))
                                        }
                                    />
                                </div>
                                <WizButton
                                    text={'가져오기'}
                                    type="border"
                                    size="small"
                                    onClick={() => setPicker({field: 'mobile_slot_id', lang: 'en'})}
                                />
                            </div>
                        </div>
                    </section>

                    {/* 외부 링크 */}
                    <section>
                        <Text className="text-h3 text-font01">외부 링크 (선택)</Text>
                        <Text className="text-body3 text-font02 mt-[4px]">url_third_party — 표시 전용</Text>
                        <UnderLineInput
                            inputClassName={'w-full text-body2'}
                            value={body.url_third_party}
                            placeholder={'https://...'}
                            onChanged={t => setBody(prev => ({...prev, url_third_party: t}))}
                        />
                    </section>
                </div>

                {/* 필요 항목 / 제출 */}
                {!canSubmit && (
                    <div className="mt-3 p-3 border border-font_red rounded-[5px] bg-tag_red_light">
                        <Text className="text-body2 text-font_red font-bold">아직 입력이 필요한 항목이 있어요</Text>
                        <div className="flex flex-wrap gap-2 mt-[8px]">
                            {missing.map(m => (
                                <Text
                                    key={m}
                                    className="text-body3 font-bold text-font_red bg-white px-[8px] py-[3px] rounded-[4px] border border-font_red">
                                    {m}
                                </Text>
                            ))}
                        </div>
                    </div>
                )}
                <div className="mt-4 flex items-center gap-x-[10px]">
                    <WizButton
                        text={
                            view === 'create'
                                ? isRolling
                                    ? `세트 만들기 (${uniq(body.windows).length}개)`
                                    : '대회 만들기'
                                : '저장'
                        }
                        type="primary"
                        size="big"
                        disabled={!canSubmit}
                        className={!canSubmit ? 'border border-line01 cursor-not-allowed' : undefined}
                        textClassName={!canSubmit ? 'text-font03' : undefined}
                        onClick={submit}
                    />
                    {isEdit && body.editKey && (
                        <WizButton text={'삭제'} type="red" size="big" onClick={onRequestDelete} />
                    )}
                </div>
            </article>

            {/* Exchange Link 누락 확인 모달 (선택 항목이라 경고 후 진행) */}
            <ConfirmDeleteModal
                open={!!linkWarn}
                title={'Exchange Link가 비어 있어요'}
                note={`Exchange Link 값이 빠졌는데 정말 ${view === 'create' ? '생성' : '저장'}하시겠습니까?`}
                warning={linkWarn ? `링크 없음: ${linkWarn.join(', ')}` : undefined}
                confirmText={view === 'create' ? '그래도 생성' : '그래도 저장'}
                confirmType="primary"
                onConfirm={() => {
                    setLinkWarn(null)
                    doSubmit()
                }}
                onCancel={() => setLinkWarn(null)}
            />

            {/* Slot 가져오기 모달 — 누른 버튼의 언어/디바이스로 조회 후 item id 적용 */}
            <BannerPickerModal
                open={!!picker}
                language={picker?.lang || 'ko'}
                device={picker?.field === 'mobile_slot_id' ? 'mo' : 'pc'}
                fieldLabel={picker?.field === 'mobile_slot_id' ? '모바일 배너' : 'PC 배너'}
                onSelect={applyPicked}
                onClose={() => setPicker(null)}
            />
        </>
    )
}

export default LeagueForm
